import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { Checkbox, Grid, IconButton, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { isValidPhoneNumber } from 'react-phone-number-input';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import StyledButton from '../Common/StyledButton';
import CustomPhoneNumberInput from '../CustomPhoneNumberInput/CustomPhoneNumberInput';
import LabeledInput from '../Common/LabeledInput';
import { passwordRequirements, sanitizeInputName } from '../../utils/userInputUtils';
import { useAppSelector } from '../../redux/hooks';
import { ICreateCoach, onboardingActions } from '../../redux/onboarding/onboardingSlice';
import { LOGIN_ROUTE } from '../../utils/routes';

const isPhoneNumberValid = (value: string | undefined) => {
  if (value) {
    return isValidPhoneNumber(value);
  }
  return true;
};

export const StyledCheckbox = styled(Checkbox)({
  color: '#6CA3AC',
  '&.Mui-checked': {
    color: '#6CA3AC',
  },
});

export default function Details() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isValidPassword, setIsValidPassword] = React.useState(false);
  const { createCoachLoading } = useAppSelector((state) => state.onboarding);
  const { affiliate } = useParams();

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(t('therapistOnboarding.validations.firstName')),
    lastName: Yup.string().required(t('therapistOnboarding.validations.lastName')),
    email: Yup.string()
      .email(t('therapistOnboarding.validations.emailInvalid'))
      .required(t('therapistOnboarding.validations.emailRequired')),
    phone: Yup.string()
      .optional()
      .test(
        'phoneNumber',
        t('therapistOnboarding.validations.phoneNumberInvalid'),
        isPhoneNumberValid
      ),
    password: Yup.string().required(t('therapistOnboarding.validations.password')),
    confirmPassword: Yup.string().required(t('therapistOnboarding.validations.confirmPassword')),
    isAgreedPrivacyPolicy: Yup.boolean().isTrue(t('therapistOnboarding.validations.privacyPolicy')),
    subscribeToNewsletter: Yup.boolean().required(),
  });

  const initialValues: ICreateCoach = {
    firstName: '',
    lastName: '',
    email: '',
    timeZone: '',
    organizationId: 'decb4748-9dba-4672-b0c2-88aad43fc307',
    password: '',
    confirmPassword: '',
    subscribeToNewsletter: true,
    isAgreedPrivacyPolicy: false,
    phone: '',
  };
  const { values, touched, errors, setFieldValue, handleChange, handleSubmit, handleBlur } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit: () => {
        const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
        dispatch(
          onboardingActions.createCoach({
            data: { ...values, timeZone, brandTag: affiliate },
            navigate,
          })
        );
      },
    });

  const handleNameChange = (field: string, value: string) => {
    handleChange(field)(sanitizeInputName(value));
  };

  const handlePhoneNumberChange = (pNumber: string) => {
    setFieldValue('phone', pNumber ?? '');
  };

  useEffect(() => {
    const isAllValid = passwordRequirements(values.password, values.confirmPassword)
      .slice(0, 5)
      .every((requirement) => requirement.isValid());

    setIsValidPassword(isAllValid);
  }, [values.password, values.confirmPassword]);

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Grid display="flex" flexDirection="column" gap="32px" pt={6}>
        <Box position="relative" width="100%" display="flex">
          <IconButton onClick={() => navigate(LOGIN_ROUTE)} sx={{ position: 'absolute', left: 0 }}>
            <img src="/assets/icons/arrow-left.svg" alt="arrow-back" />
          </IconButton>
          <Typography variant="title2Bold" color="primary" textAlign="center" width="100%">
            {t('common.signUp')}
          </Typography>
        </Box>

        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <LabeledInput
                label={t('therapistOnboarding.firstName')}
                id="first-name"
                type="text"
                name="firstName"
                value={values.firstName}
                handleChange={(event) => handleNameChange('firstName', event.target.value)}
                onBlur={handleBlur}
                error={touched.firstName ? errors.firstName : undefined}
                placeholder={t('therapistOnboarding.firstName')}
                disabled={createCoachLoading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LabeledInput
                label={t('therapistOnboarding.lastName')}
                id="last-name"
                type="text"
                name="lastName"
                value={values.lastName}
                handleChange={(event) => handleNameChange('lastName', event.target.value)}
                onBlur={handleBlur}
                error={touched.lastName ? errors.lastName : undefined}
                placeholder={t('therapistOnboarding.lastName')}
                disabled={createCoachLoading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LabeledInput
                label={t('therapistOnboarding.email')}
                id="email"
                type="email"
                value={values.email}
                handleChange={(e) => handleChange('email')(e.target.value.toLowerCase())}
                onBlur={handleBlur}
                error={touched.email ? errors.email : undefined}
                placeholder={t('therapistOnboarding.email')}
                disabled={createCoachLoading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomPhoneNumberInput
                value={values.phone}
                handleChange={handlePhoneNumberChange}
                onBlur={handleBlur}
                error={touched.phone ? errors.phone : undefined}
                optional
                disabled={createCoachLoading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LabeledInput
                label={t('auth.password')}
                name="password"
                placeholder={t('auth.password')}
                type={showPassword ? 'text' : 'password'}
                id="new-password"
                value={values.password}
                handleChange={handleChange}
                onBlur={handleBlur}
                error={touched.password ? errors.password : undefined}
                endIcon={
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => {
                      setShowPassword(!showPassword);
                    }}
                  >
                    {showPassword ? (
                      <img src="/assets/icons/visibility.svg" alt="visibility" />
                    ) : (
                      <img src="/assets/icons/visibility-off.svg" alt="visibility-off" />
                    )}
                  </IconButton>
                }
                disabled={createCoachLoading}
              />
              {values.password && !isValidPassword && (
                <Typography variant="caption2Regular" color="red">
                  {t('therapistOnboarding.validations.weakPassword')}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <LabeledInput
                label={t('changePassword.confirmPassword')}
                name="confirmPassword"
                placeholder={t('changePassword.confirmPassword')}
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirm-password"
                value={values.confirmPassword}
                handleChange={handleChange}
                onBlur={handleBlur}
                error={touched.confirmPassword ? errors.confirmPassword : undefined}
                endIcon={
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => {
                      setShowConfirmPassword(!showConfirmPassword);
                    }}
                  >
                    {showConfirmPassword ? (
                      <img src="/assets/icons/visibility.svg" alt="visibility" />
                    ) : (
                      <img src="/assets/icons/visibility-off.svg" alt="visibility-off" />
                    )}
                  </IconButton>
                }
                disabled={createCoachLoading}
              />
              {values.password &&
                values.confirmPassword &&
                values.password !== values.confirmPassword && (
                  <Typography variant="caption2Regular" color="red">
                    {t('therapistOnboarding.validations.passwordMismatch')}
                  </Typography>
                )}
            </Grid>
          </Grid>
          <Box />
        </Box>

        {!createCoachLoading && (
          <Grid display="flex" flexDirection="column" justifyContent="center">
            <Typography variant="bodyBold" color="primary">
              {`${t('changePassword.passwordValidations.PasswordMustContain')}`}
            </Typography>
            <Grid container spacing={1.5} pt={1.5}>
              {passwordRequirements(values.password, values.confirmPassword)
                .slice(0, 5)
                .map((requirement) => (
                  <Grid item container xs={12} md={6} key={requirement.text}>
                    <img
                      src={`/assets/icons/${
                        requirement.isValid() ? 'check-circle-new' : 'close-circle'
                      }.svg`}
                      alt="check-circle-new"
                    />
                    <Typography variant="bodyRegular" color="primary" pl={2}>
                      {requirement.text}
                    </Typography>
                  </Grid>
                ))}
            </Grid>
          </Grid>
        )}
        <Grid container pt={2} spacing={{ xs: 2, md: 4 }} justifyContent="end">
          <Grid item xs={12} md={4.5} textAlign="center">
            <StyledCheckbox
              name="isAgreedPrivacyPolicy"
              checked={values.isAgreedPrivacyPolicy}
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{ 'aria-label': 'controlled' }}
              checkedIcon={<CheckBoxOutlinedIcon />}
            />
            <>
              <Typography variant="tinyTexts">
                {t('therapistOnboarding.privacyPolicyLabel')}
              </Typography>
              <Typography
                variant="tinyTexts"
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => {
                  window.open('https://www.progressmagic.com/privacy-policy');
                }}
              >
                {t('therapistOnboarding.privacyPolicy')}
              </Typography>
            </>
          </Grid>
          <Grid item xs={12} md={4.5} textAlign="center">
            <StyledCheckbox
              name="subscribeToNewsletter"
              checked={values.subscribeToNewsletter}
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{ 'aria-label': 'controlled' }}
              checkedIcon={<CheckBoxOutlinedIcon />}
            />
            <Typography variant="tinyTexts">
              {t('therapistOnboarding.subscribeToNewsLetter')}
            </Typography>
          </Grid>
          <Grid item xs={12} md={createCoachLoading ? 3 : 2.5}>
            <StyledButton
              paddingY={14}
              fullWidth
              disabled={!isValidPassword}
              loading={createCoachLoading}
              loadingText={t('common.signingUp')}
            >
              {t('common.signUp')}
            </StyledButton>
          </Grid>
        </Grid>

        {touched.isAgreedPrivacyPolicy && errors.isAgreedPrivacyPolicy && (
          <Typography
            sx={{
              background: 'linear-gradient(175.46deg, #FF9A9A -9.81%, #FF5858 96.32%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
            variant="bodyBold"
            textAlign="center"
          >
            {errors.isAgreedPrivacyPolicy}
          </Typography>
        )}
      </Grid>
    </form>
  );
}
